import { useState, useEffect, useMemo } from "react";
import { Layers, Monitor, Cpu, Building2, Scale, BookOpen, ChevronRight, TrendingUp, TrendingDown, Minus, Globe, Zap, Users, Heart } from "lucide-react";
import { AICCIMethodologyLink } from "../dashboard/AICCIMethodology";
import { ResearchProductGap } from "../research/ResearchProductGap";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

import { ObservatoryLoader } from "../common/ObservatoryLoader";
import { DashboardSkeleton, Shimmer } from "../common/DashboardSkeleton";
import { usePrefetchedData } from "../common/DataPrefetch";

const GALAXY_META = {
  application: { label: "AI Apps", icon: Monitor, color: "#34d399", desc: "Software applications" },
  device: { label: "Physical AI", icon: Cpu, color: "#60a5fa", desc: "Hardware, wearables, robotics" },
  company: { label: "Companies", icon: Building2, color: "#f59e0b", desc: "Organizations building AI" },
  policy: { label: "Policy", icon: Scale, color: "#06b6d4", desc: "Regulations & governance" },
  research: { label: "Research", icon: BookOpen, color: "#ec4899", desc: "Papers & programs" },
};

const ORIENT_COLORS = { care: "#34d399", utility: "#60a5fa", creative: "#a78bfa", extraction: "#f87171" };
const DIM_COLORS = { care: "#34d399", diversity: "#ec4899", governance: "#8b5cf6", geographic: "#3b82f6", extraction: "#f59e0b", inclusion: "#06b6d4", agency: "#f97316" };

export const ConsciousnessDashboard = ({ setActiveGalaxy }) => {
  const prefetched = usePrefetchedData();
  const [overview, setOverview] = useState(null);
  const [globalAicci, setGlobalAicci] = useState(null);
  const [deviceAicci, setDeviceAicci] = useState(null);
  const [civilizationAicci, setCivilizationAicci] = useState(null);
  const [swInclusion, setSwInclusion] = useState(null);
  const [hwInclusion, setHwInclusion] = useState(null);
  const [researchCii, setResearchCii] = useState(null);
  const [companiesCii, setCompaniesCii] = useState(null);
  const [policyCii, setPolicyCii] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Sync from prefetch store as data arrives. Dashboard exits the loading
  // state as soon as we have ANY count source (overview OR pulse) — pulse
  // alone is enough to render the observatory cards via the resolvedByType
  // fallback. Secondary widgets (AICCI, inclusion) render with their own
  // skeletons as their data arrives.
  useEffect(() => {
    if (prefetched.galaxyOverview && !overview) setOverview(prefetched.galaxyOverview);
    if (prefetched.pulse && !pulse) setPulse(prefetched.pulse);
    if (prefetched.aicciApp && !globalAicci) setGlobalAicci(prefetched.aicciApp);
    if (prefetched.aicciDevice && !deviceAicci) setDeviceAicci(prefetched.aicciDevice);
    if (prefetched.aicciGlobal && !civilizationAicci) setCivilizationAicci(prefetched.aicciGlobal);
    if (prefetched.swInclusion && !swInclusion) setSwInclusion(prefetched.swInclusion);
    if (prefetched.hwInclusion && !hwInclusion) setHwInclusion(prefetched.hwInclusion);
    if (prefetched.researchCii && !researchCii) setResearchCii(prefetched.researchCii);
    if (prefetched.companyStats && !companiesCii) setCompaniesCii(prefetched.companyStats);
    if (prefetched.policyStats && !policyCii) setPolicyCii(prefetched.policyStats);
    if ((prefetched.galaxyOverview || prefetched.pulse || overview || pulse) && loading) setLoading(false);
  }, [prefetched, overview, pulse, globalAicci, deviceAicci, civilizationAicci, swInclusion, hwInclusion, researchCii, companiesCii, policyCii, loading]);

  // Last-resort safety: if prefetch hasn't delivered ANY count source after 4s
  // (bootstrap typically arrives in <500ms), fire pulse + overview directly.
  // All other widgets are covered by DataPrefetch — no need to re-fetch them here.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (overview || pulse) { setLoading(false); return; }
      Promise.allSettled([
        axios.get(`${API}/galaxy/overview`),
        axios.get(`${API}/pulse`),
      ]).then(([o, p]) => {
        if (o.status === "fulfilled") setOverview(prev => prev || o.value.data);
        if (p.status === "fulfilled") setPulse(prev => prev || p.value.data);
      }).finally(() => setLoading(false));
    }, 4000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pulse-based fallback: if a stale precomputed `galaxy_overview` entry is missing
  // newer galaxies (company/policy/research), synthesize entries from /api/pulse so
  // cards never show "Coming soon" for populated observatories.
  const resolvedByType = useMemo(() => {
    const PULSE_COUNT_KEY = { application: "total_apps", device: "total_devices", company: "total_companies", policy: "total_policies", research: "total_papers" };
    const liveByType = Object.fromEntries((overview?.galaxies || []).map(g => [g.entity_type, g]));
    const out = {};
    for (const et of Object.keys(GALAXY_META)) {
      const live = liveByType[et];
      if (live && live.count > 0) { out[et] = live; continue; }
      const cnt = pulse?.[PULSE_COUNT_KEY[et]];
      if (cnt) {
        const carePct = et === "research" ? (pulse?.papers_care_pct ?? null) : null;
        out[et] = { entity_type: et, count: cnt, care_pct: carePct, orientation: null };
      } else {
        out[et] = null;
      }
    }
    return out;
  }, [overview, pulse]);
  const activeGalaxies = useMemo(
    () => (overview?.galaxies || []).filter(g => g.count > 0 && g.entity_type !== "tool"),
    [overview]
  );
  const humanityRows = useMemo(
    () => Object.entries(resolvedByType).filter(([, g]) => g && g.count > 0),
    [resolvedByType]
  );

  if (loading) return <DashboardSkeleton galaxyColor="violet" />;

  const totalEntities = overview?.total_entities || 0;
  const swData = activeGalaxies.find(g => g.entity_type === "application");
  const hwData = activeGalaxies.find(g => g.entity_type === "device");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="consciousness-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl py-10 px-6 md:px-10 text-center"
        style={{ background: "linear-gradient(135deg, #0f0a1e 0%, #0c1222 40%, #0a1628 100%)", border: "1px solid rgba(139,92,246,0.15)" }}>
        <p className="text-[10px] font-bold text-violet-400/70 uppercase tracking-[0.25em] mb-3">Amplifying Conscious Innovation</p>
        <h1 className="text-2xl md:text-4xl font-bold text-white mb-3">The Consciousness Dashboard</h1>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto mb-2">
          A unified view across {activeGalaxies.length} observatories tracking {(pulse?.total_entities || totalEntities).toLocaleString()} AI entities in software, hardware, companies, policy, and research.
        </p>
        <p className="text-xs text-violet-400/50 italic mb-5">Amplifying humanity. De-weaponizing progress.</p>
        <div className="flex flex-wrap justify-center gap-2.5">
          <button onClick={() => document.getElementById('consciousness-content')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white text-xs md:text-sm font-semibold rounded-lg transition-colors shadow-lg shadow-violet-500/20"
            data-testid="cta-explore-universe">
            Explore the Data
          </button>
          <button onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'about' }))}
            className="px-4 py-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.1] text-white text-xs md:text-sm font-medium rounded-lg transition-colors"
            data-testid="cta-about-observatory">
            About the Observatory
          </button>
        </div>
      </div>

      <div id="consciousness-content" />
      {/* Global Pulse */}
      {pulse ? (
        <div className="relative overflow-hidden rounded-2xl" data-testid="global-pulse"
          style={{ background: "linear-gradient(90deg, rgba(15,10,30,0.95) 0%, rgba(12,18,34,0.95) 50%, rgba(10,16,40,0.95) 100%)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.04]">
            <Zap className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] font-bold text-amber-400/80 uppercase tracking-[0.2em]">Global Pulse</span>
            <span className="text-[8px] text-slate-500 ml-auto">Live observatory stats</span>
          </div>
          <div className="relative overflow-hidden">
            <div className="items-center gap-6 px-4 py-3 whitespace-nowrap" data-testid="pulse-ticker"
              style={{ display: "inline-flex", animation: "marquee 35s linear infinite" }}>
              {[0, 1].map(dup => (
                <span key={dup} className="inline-flex items-center gap-6">
                  <span className="flex items-center gap-2 text-xs">
                    <span className="text-slate-400">Tracking</span>
                    <span className="text-white font-bold tabular-nums">{pulse.total_entities?.toLocaleString()}</span>
                    <span className="text-slate-400">AI entities</span>
                  </span>
                  <span className="w-px h-3 bg-white/[0.08]" />
                  <span className="flex items-center gap-2 text-xs">
                    <span className="text-slate-400">Software</span>
                    <span className="text-emerald-400 font-bold tabular-nums">{pulse.total_apps?.toLocaleString()}</span>
                    <span className="text-slate-500">|</span>
                    <span className="text-slate-400">Hardware</span>
                    <span className="text-blue-400 font-bold tabular-nums">{pulse.total_devices?.toLocaleString()}</span>
                  </span>
                  <span className="w-px h-3 bg-white/[0.08]" />
                  <span className="flex items-center gap-2 text-xs">
                    <span className="text-slate-400">This week</span>
                    <span className="text-amber-400 font-bold tabular-nums">+{pulse.new_this_week?.toLocaleString()}</span>
                    <span className="text-slate-400">new</span>
                  </span>
                  <span className="w-px h-3 bg-white/[0.08]" />
                  <span className="flex items-center gap-2 text-xs">
                    <Globe className="w-3 h-3 text-slate-400" />
                    <span className="text-white font-semibold">{pulse.countries_count}</span>
                    <span className="text-slate-400">countries</span>
                  </span>
                  <span className="w-px h-3 bg-white/[0.08]" />
                  <span className="flex items-center gap-2 text-xs">
                    <Users className="w-3 h-3 text-slate-400" />
                    <span className="text-slate-400">Top:</span>
                    {pulse.top_countries?.map((c, ci) => (
                      <span key={ci} className="text-slate-300 font-medium">{c.country}{ci < pulse.top_countries.length - 1 ? ", " : ""}</span>
                    ))}
                  </span>
                  <span className="w-px h-3 bg-white/[0.08]" />
                  <span className="flex items-center gap-2 text-xs">
                    <Heart className="w-3 h-3 text-emerald-400" />
                    <span className="text-emerald-400 font-bold">{pulse.care_pct}%</span>
                    <span className="text-slate-400">care-oriented</span>
                  </span>
                  <span className="w-px h-3 bg-white/[0.08]" />
                  <span className="flex items-center gap-2 text-xs">
                    <span className="text-slate-400">AICCI</span>
                    <span className="text-violet-400 font-bold">{civilizationAicci?.score ?? pulse.aicci_global}</span>
                    <span className="text-slate-500">/ 100</span>
                  </span>
                  <span className="w-px h-3 bg-white/[0.08]" />
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(15,10,30,0.95)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.04]">
            <Zap className="w-3 h-3 text-amber-400/40" />
            <div className="h-2.5 w-20 bg-white/[0.04] animate-pulse rounded" />
          </div>
          <div className="px-4 py-3 flex items-center gap-6">
            {[1,2,3,4,5].map(i => <div key={i} className="h-3 w-24 bg-white/[0.04] animate-pulse rounded" />)}
          </div>
        </div>
      )}

      {/* AI Civilization CII — show as soon as available */}
      {civilizationAicci ? (
        <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(139,92,246,0.12)" }}>
          <div className="p-5 md:p-6">
            <div className="flex items-center gap-2 mb-5">
              <p className="text-[10px] font-bold text-violet-400/70 uppercase tracking-[0.2em]">Conscious Innovation Index</p>
              <AICCIMethodologyLink entityType="" />
            </div>

            {/* AI Civilization Score — combined */}
            <div className="bg-gradient-to-r from-violet-500/[0.06] to-amber-500/[0.04] border border-violet-500/15 rounded-xl p-5 mb-5 text-center">
              <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-[0.25em] mb-3">AI Civilization Conscious Innovation Index</p>
              <div className="relative w-24 h-24 mx-auto mb-3 flex items-center justify-center">
                <svg width="96" height="96" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#a78bfa" strokeWidth="5"
                    strokeDasharray={`${civilizationAicci.score * 2.64} ${264 - civilizationAicci.score * 2.64}`}
                    strokeDashoffset="66" strokeLinecap="round" />
                </svg>
                <span className="absolute text-3xl font-black text-violet-300">{civilizationAicci.score}</span>
              </div>
              <p className="text-xs text-slate-400">{(pulse?.total_entities || civilizationAicci.total_apps || 0).toLocaleString()} entities across all observatories</p>
              <p className="text-[10px] text-slate-500 mt-1">7-dimension composite: care, diversity, governance, geographic, extraction, inclusion, agency</p>
            </div>

            {/* Expandable 7-Dimension Breakdown */}
            {civilizationAicci.dimensions && (
              <div className="mt-4">
                <button 
                  onClick={() => setShowBreakdown(!showBreakdown)}
                  className="text-xs text-violet-400 hover:text-violet-300 font-medium inline-flex items-center gap-1 mb-3"
                  data-testid="consciousness-breakdown-toggle"
                >
                  {showBreakdown ? "Hide breakdown" : "See breakdown"} →
                </button>
                {showBreakdown && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 animate-fade-in">
                    {Object.entries(civilizationAicci.dimensions).map(([key, dim]) => (
                      <div key={key} className="flex items-center gap-3 bg-white/[0.02] rounded-lg px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{dim.label}</span>
                            <span className="text-xs font-bold" style={{ color: DIM_COLORS[key] || "#a78bfa" }}>{dim.score}/100</span>
                          </div>
                          <div className="w-full h-1.5 bg-white/[0.06] rounded-full">
                            <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${dim.score}%`, backgroundColor: DIM_COLORS[key] || "#a78bfa" }} />
                          </div>
                          <p className="text-[8px] text-slate-500 mt-0.5">Weight: {(dim.weight * 100)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden p-5 md:p-6" style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(139,92,246,0.12)" }}>
          <Shimmer className="h-3 w-40 mb-5" />
          <div className="bg-violet-500/[0.04] border border-violet-500/10 rounded-xl p-5 mb-5 text-center space-y-3">
            <Shimmer className="h-3 w-56 mx-auto" />
            <Shimmer className="h-24 w-24 mx-auto rounded-full" />
            <Shimmer className="h-3 w-48 mx-auto" />
          </div>
        </div>
      )}

      {/* Galaxy CII Comparison — show when available */}
      {globalAicci && deviceAicci ? (
        <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(139,92,246,0.08)" }}>
          <div className="p-5 md:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Software AICCI */}
              <div className="bg-white/[0.03] border border-emerald-500/10 rounded-xl p-4 text-center cursor-pointer hover:border-emerald-500/30 transition-colors"
                onClick={() => setActiveGalaxy("apps")}>
                <div className="relative w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#34d399" strokeWidth="4"
                      strokeDasharray={`${globalAicci.score * 1.76} ${176 - globalAicci.score * 1.76}`}
                      strokeDashoffset="44" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(52,211,153,0.4))" }} />
                  </svg>
                  <span className="absolute text-lg font-black text-emerald-400">{globalAicci.score}</span>
                </div>
                <p className="text-xs font-bold text-emerald-400">AI Software</p>
                <p className="text-[10px] text-slate-400">{swData?.count?.toLocaleString()} apps</p>
              </div>

              {/* Device AICCI */}
              <div className="bg-white/[0.03] border border-blue-500/10 rounded-xl p-4 text-center cursor-pointer hover:border-blue-500/30 transition-colors"
                onClick={() => setActiveGalaxy("hardware")}>
                <div className="relative w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#60a5fa" strokeWidth="4"
                      strokeDasharray={`${deviceAicci.score * 1.76} ${176 - deviceAicci.score * 1.76}`}
                      strokeDashoffset="44" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(96,165,250,0.4))" }} />
                  </svg>
                  <span className="absolute text-lg font-black text-blue-400">{deviceAicci.score}</span>
                </div>
                <p className="text-xs font-bold text-blue-400">Physical AI</p>
                <p className="text-[10px] text-slate-400">{hwData?.count?.toLocaleString()} devices</p>
              </div>

              {/* Research CII */}
              <div className="bg-white/[0.03] border border-pink-500/10 rounded-xl p-4 text-center cursor-pointer hover:border-pink-500/30 transition-colors"
                onClick={() => setActiveGalaxy("research")}>
                <div className="relative w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#ec4899" strokeWidth="4"
                      strokeDasharray={`${(researchCii?.score || 0) * 1.76} ${176 - (researchCii?.score || 0) * 1.76}`}
                      strokeDashoffset="44" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(236,72,153,0.4))" }} />
                  </svg>
                  <span className="absolute text-lg font-black text-pink-400">{researchCii?.score || "—"}</span>
                </div>
                <p className="text-xs font-bold text-pink-400">Research</p>
                <p className="text-[10px] text-slate-400">{(researchCii?.total || 0).toLocaleString()} papers</p>
              </div>

              {/* Companies CII */}
              <div className="bg-white/[0.03] border border-amber-500/10 rounded-xl p-4 text-center cursor-pointer hover:border-amber-500/30 transition-colors"
                onClick={() => setActiveGalaxy("companies")}>
                <div className="relative w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#f59e0b" strokeWidth="4"
                      strokeDasharray={`${(companiesCii?.avg_cii || 0) * 1.76} ${176 - (companiesCii?.avg_cii || 0) * 1.76}`}
                      strokeDashoffset="44" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(245,158,11,0.4))" }} />
                  </svg>
                  <span className="absolute text-lg font-black text-amber-400">{companiesCii?.avg_cii || "—"}</span>
                </div>
                <p className="text-xs font-bold text-amber-400">Companies</p>
                <p className="text-[10px] text-slate-400">{(companiesCii?.total || 0).toLocaleString()} companies</p>
              </div>

              {/* Policy CII */}
              <div className="bg-white/[0.03] border border-violet-500/10 rounded-xl p-4 text-center cursor-pointer hover:border-violet-500/30 transition-colors"
                onClick={() => setActiveGalaxy("policy")}>
                <div className="relative w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#a78bfa" strokeWidth="4"
                      strokeDasharray={`${(policyCii?.avg_cii || 0) * 1.76} ${176 - (policyCii?.avg_cii || 0) * 1.76}`}
                      strokeDashoffset="44" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(167,139,250,0.4))" }} />
                  </svg>
                  <span className="absolute text-lg font-black text-violet-400">{policyCii?.avg_cii || "—"}</span>
                </div>
                <p className="text-xs font-bold text-violet-400">Policy</p>
                <p className="text-[10px] text-slate-400">{policyCii?.total_policies || 0} frameworks</p>
              </div>

              {/* Insight */}
              <div className="bg-violet-500/[0.04] border border-violet-500/10 rounded-xl p-4 flex flex-col justify-center col-span-2 sm:col-span-1">
                <p className="text-[10px] font-bold text-violet-400 uppercase tracking-wider mb-1">Highest CII</p>
                <p className="text-2xl font-black text-violet-400 mb-1">
                  {(() => {
                    const scores = [
                      { name: "Physical AI", score: deviceAicci?.score || 0 },
                      { name: "Software", score: globalAicci?.score || 0 },
                      { name: "Research", score: researchCii?.score || 0 },
                      { name: "Companies", score: companiesCii?.avg_cii || 0 },
                      { name: "Policy", score: policyCii?.avg_cii || 0 },
                    ].sort((a, b) => b.score - a.score);
                    return scores[0].name;
                  })()}
                </p>
                <p className="text-[10px] text-slate-400 leading-relaxed">
                  {(() => {
                    const scores = [
                      { name: "Physical AI", score: deviceAicci?.score || 0 },
                      { name: "Software", score: globalAicci?.score || 0 },
                      { name: "Research", score: researchCii?.score || 0 },
                      { name: "Companies", score: companiesCii?.avg_cii || 0 },
                      { name: "Policy", score: policyCii?.avg_cii || 0 },
                    ].filter(s => s.score > 0).sort((a, b) => b.score - a.score);
                    const top = scores[0];
                    const bottom = scores[scores.length - 1];
                    return top && bottom ? `${top.name} leads with ${top.score}, while ${bottom.name} trails at ${bottom.score}.` : "";
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden p-5 md:p-6" style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(139,92,246,0.08)" }}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center space-y-2">
                <Shimmer className="h-16 w-16 mx-auto rounded-full" />
                <Shimmer className="h-3 w-16 mx-auto" />
                <Shimmer className="h-2.5 w-20 mx-auto" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Value Orientation Categories */}

      {/* How We Classify */}
      <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(139,92,246,0.08)" }} data-testid="orientation-categories">
        <div className="p-5 md:p-6">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-4">How We Classify AI Orientation</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { key: "care", color: "#34d399", label: "Care", desc: "AI built to heal, protect, include, and empower. Prioritizes human wellbeing, accessibility, and equity over profit. Examples: assistive tech, mental health tools, disaster response AI." },
              { key: "utility", color: "#60a5fa", label: "Utility", desc: "AI built for practical function and productivity. Neutral in orientation — neither explicitly caring nor extractive. Automates tasks, improves efficiency. Examples: code assistants, search engines, translation tools." },
              { key: "creative", color: "#a78bfa", label: "Creative", desc: "AI that augments human expression and imagination. Generates art, music, stories, and new forms of culture. Amplifies creativity rather than replacing it. Examples: image generators, music composition, writing aids." },
              { key: "extraction", color: "#f87171", label: "Extraction", desc: "AI designed to surveil, manipulate, exploit, or weaponize. Prioritizes control, addiction, and data harvesting over human agency. Examples: deepfake tools, manipulative ad targeting, autonomous weapons." },
            ].map(cat => (
              <div key={cat.key} className="bg-white/[0.02] border rounded-xl p-4" style={{ borderColor: `${cat.color}20` }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: cat.color, boxShadow: `0 0 10px ${cat.color}40` }} />
                  <p className="text-xs font-bold" style={{ color: cat.color }}>{cat.label}</p>
                  {overview && (
                    <span className="text-[9px] text-slate-500 ml-auto">
                      {(() => {
                        const totals = {}; let sum = 0;
                        (overview.galaxies || []).forEach(g => {
                          if (!g.orientation || !["application", "device"].includes(g.entity_type)) return;
                          Object.entries(g.orientation).forEach(([k, v]) => {
                            if (!k) return;
                            const kk = k.toLowerCase();
                            totals[kk] = (totals[kk] || 0) + v; sum += v;
                          });
                        });
                        return totals[cat.key] && sum ? `${(totals[cat.key] / sum * 100).toFixed(1)}%` : "—";
                      })()}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Galaxy Cards */}
      <div>
        <h2 className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mb-4">The Observatories</h2>
        <div className="fluid-grid-cards">
          {Object.entries(GALAXY_META).map(([et, meta]) => {
            const data = resolvedByType[et];
            const isActive = data && data.count > 0;
            const Icon = meta.icon;
            return (
              <div key={et}
                onClick={() => isActive && setActiveGalaxy(et === "application" ? "apps" : et === "device" ? "hardware" : et === "company" ? "companies" : et === "research" ? "research" : et)}
                data-testid={`galaxy-card-${et}`}
                className={`rounded-xl border p-5 transition-all ${
                  isActive ? "bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15] cursor-pointer hover:bg-white/[0.05]" : "bg-white/[0.01] border-white/[0.04] opacity-50"
                }`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${meta.color}15` }}>
                      <Icon className="w-5 h-5" style={{ color: meta.color }} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-200">{meta.label}</h3>
                      <p className="text-[10px] text-slate-500">{meta.desc}</p>
                    </div>
                  </div>
                  {isActive && <ChevronRight className="w-4 h-4 text-slate-500" />}
                </div>
                {isActive ? (
                  <>
                    <p className="text-2xl font-black mb-2" style={{ color: meta.color }}>{data.count.toLocaleString()}</p>
                    {data.orientation ? (
                      <div className="flex h-2 rounded-full overflow-hidden mb-2">
                        {Object.entries(data.orientation).map(([o, c]) => (
                          <div key={o} style={{ width: `${(c / data.count) * 100}%`, background: ORIENT_COLORS[o] || "#64748b" }} />
                        ))}
                      </div>
                    ) : (
                      <div className="h-2 rounded-full mb-2" style={{ background: `linear-gradient(90deg, ${meta.color}40, ${meta.color}20)` }} />
                    )}
                    <p className="text-[10px] text-slate-400">
                      {data.care_pct != null ? `${data.care_pct}% care-oriented` : "Live data · open to explore"}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-slate-700 italic mt-2">Coming soon</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* How is humanity thinking... */}
      {humanityRows.length > 0 && (
        <div>
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mb-4">How is humanity thinking in the language of...</h2>
          <div className="space-y-3">
            {humanityRows.map(([et, g]) => {
              const meta = GALAXY_META[et];
              if (!meta) return null;
              const total = g.count;
              const care = g.orientation?.care || 0;
              const utility = g.orientation?.utility || 0;
              const Icon = meta.icon;
              return (
                <div key={et} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:bg-white/[0.04] transition-colors"
                  onClick={() => setActiveGalaxy(et === "application" ? "apps" : et === "device" ? "hardware" : et === "company" ? "companies" : et === "research" ? "research" : et)}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}15` }}>
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">In the language of {meta.label}</p>
                    <p className="text-sm text-slate-300 mt-0.5">
                      {et === "application" && g.orientation && `${((utility/total)*100).toFixed(0)}% utility, ${((care/total)*100).toFixed(0)}% wellbeing. ${total.toLocaleString()} apps tell a story of efficiency over empathy.`}
                      {et === "device" && g.orientation && `${((care/total)*100).toFixed(1)}% care-oriented vs ${((utility/total)*100).toFixed(1)}% utility. ${total.toLocaleString()} devices. Hardware embodies more empathy than software.`}
                      {et === "company" && `${total.toLocaleString()} companies tracked.${g.care_pct != null ? ` ${g.care_pct}% care-oriented.` : ""} Who builds AI shapes what AI becomes.`}
                      {et === "research" && `${total.toLocaleString()} papers classified.${g.care_pct != null ? ` ${g.care_pct}% care-oriented.` : ""} Academic research sets the ethical foundation for tomorrow's products.`}
                      {et === "policy" && `${total.toLocaleString()} policy frameworks across ${policyCii?.countries_count || 'many'} countries.${g.care_pct != null ? ` ${g.care_pct}% care-oriented.` : ""} Governance shapes what AI is allowed to become.`}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cross-observatory insights */}
      {activeGalaxies.length > 1 && globalAicci && deviceAicci && (
        <div className="space-y-3">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">What is Humanity Solving For?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-emerald-500/[0.04] border border-emerald-500/10 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-400 mb-1">The Empathy Gap</p>
              <p className="text-sm text-slate-300">Physical AI is {hwData?.care_pct}% care-oriented vs software at {swData?.care_pct}%. When we build things people can touch, we build with {(hwData?.care_pct / swData?.care_pct).toFixed(1)}x more empathy.</p>
            </div>
            <div className="bg-blue-500/[0.04] border border-blue-500/10 rounded-xl p-4">
              <p className="text-xs font-bold text-blue-400 mb-1">The Consciousness Score</p>
              <p className="text-sm text-slate-300">Hardware scores {deviceAicci.score}/100 vs software at {globalAicci.score}/100. The physical world demands more humanity in its AI than the digital one.</p>
            </div>
            {companiesCii && (
              <div className="bg-amber-500/[0.04] border border-amber-500/10 rounded-xl p-4">
                <p className="text-xs font-bold text-amber-400 mb-1">The Corporate Signal</p>
                <p className="text-sm text-slate-300">{(companiesCii.total || 0).toLocaleString()} companies score {companiesCii.avg_cii}/100 on consciousness. {companiesCii.orientations?.care?.toLocaleString() || 0} are care-oriented. Corporate AI reveals institutional values.</p>
              </div>
            )}
            {policyCii && (
              <div className="bg-cyan-500/[0.04] border border-cyan-500/10 rounded-xl p-4">
                <p className="text-xs font-bold text-cyan-400 mb-1">The Governance Compass</p>
                <p className="text-sm text-slate-300">{policyCii.total_policies} policy frameworks score {policyCii.avg_cii}/100 across {policyCii.countries_count} countries. Policy leads consciousness at {policyCii.avg_cii} vs products at {globalAicci.score}.</p>
              </div>
            )}
            {researchCii && (
              <div className="bg-pink-500/[0.04] border border-pink-500/10 rounded-xl p-4">
                <p className="text-xs font-bold text-pink-400 mb-1">The Research Foundation</p>
                <p className="text-sm text-slate-300">{(researchCii.total || 0).toLocaleString()} research papers score {researchCii.score}/100. Research is {researchCii.dimensions?.care?.raw > (swData?.care_pct || 9) ? 'leading' : 'lagging'} products in care orientation.</p>
              </div>
            )}
            <div className="bg-violet-500/[0.04] border border-violet-500/10 rounded-xl p-4">
              <p className="text-xs font-bold text-violet-400 mb-1">The Question</p>
              <p className="text-sm text-slate-300 italic">As AI scales from {(pulse?.total_entities || totalEntities).toLocaleString()} entities across {activeGalaxies.length} galaxies, will we amplify humanity or optimize it away?</p>
            </div>
            {swInclusion && hwInclusion && (
              <div className="bg-cyan-500/[0.04] border border-cyan-500/10 rounded-xl p-4 md:col-span-2">
                <p className="text-xs font-bold text-cyan-400 mb-1">The Inclusion Signal</p>
                <p className="text-sm text-slate-300">
                  Physical AI dedicates {hwInclusion.accessibility?.pct}% to accessibility vs software at {swInclusion.accessibility?.pct}%.
                  That's {hwInclusion.comparison?.ratio}x more devices serving the blind, deaf, mobility-impaired, and elderly.
                  {hwInclusion.accessibility?.count} hardware devices confront human limitation directly. {swInclusion.accessibility?.count} apps do the same.
                  When technology extends the human form, inclusion is not optional.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* The Mirror */}
      <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0f0a1e 0%, #0c1222 50%, #0a0e1a 100%)", border: "1px solid rgba(139,92,246,0.12)" }}>
        <div className="p-6 md:p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-violet-500/10 flex items-center justify-center mb-4" style={{ border: "2px solid rgba(139,92,246,0.3)" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(200,180,255,0.6)", boxShadow: "0 0 12px rgba(139,92,246,0.6)" }} />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">The Mirror</h2>
          <p className="text-xs text-violet-400/70 uppercase tracking-[0.2em] mb-3">What AI tells us about ourselves</p>
          <p className="text-sm text-slate-400 max-w-lg mx-auto mb-5">
            {(pulse?.total_entities || totalEntities).toLocaleString()} AI entities across {activeGalaxies.length} galaxies. Software scores {globalAicci?.score}/100 on consciousness. Hardware scores {deviceAicci?.score}/100. Policy leads at {policyCii?.avg_cii || '—'}/100.
          </p>
          <button onClick={() => { setActiveGalaxy("apps"); setTimeout(() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'mirror' })), 100); }}
            className="px-5 py-2 bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/30 text-violet-300 text-sm font-medium rounded-lg transition-all">
            Explore The Mirror
          </button>
        </div>
      </div>

      {/* Constellation */}
      <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #060a12 0%, #0c1a2e 50%, #0a1020 100%)", border: "1px solid rgba(52,211,153,0.12)" }}>
        <div className="p-6 md:p-8 text-center">
          <p className="text-xs font-bold text-emerald-400/70 uppercase tracking-[0.2em] mb-3">Constellation</p>
          <h2 className="text-xl font-bold text-white mb-2">Map Your Idea Across the Universe</h2>
          <p className="text-sm text-slate-400 max-w-lg mx-auto mb-5">
            Discover software apps, physical devices, and research that can bring your idea to life. {(pulse?.total_entities || totalEntities).toLocaleString()} entities mapped as an interconnected constellation.
          </p>
          <button onClick={() => { setActiveGalaxy("apps"); setTimeout(() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'constellation' })), 100); }}
            className="px-5 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 text-sm font-medium rounded-lg transition-all">
            Launch Constellation
          </button>
        </div>
      </div>

      {/* Research-Product Gap */}
      <ResearchProductGap compact />

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] text-slate-500 pt-4">
        <span>{typeof window !== "undefined" ? window.location.origin.replace("https://", "") : ""}</span>
        <span>&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
      </div>
    </div>
  );
};

export default ConsciousnessDashboard;
